import { Trans } from '@lingui/macro'
import { Currency, CurrencyAmount } from '@uniswap/sdk-core'
import { LoadingRows } from 'components/Loader/styled'
import { NetworkAlert } from 'components/NetworkAlert/NetworkAlert'
import UnstakeHeader from 'components/Stake/UnstakeHeader'
import UnstakeInputPanel from 'components/UnstakeInputPanel.tsx'
import { SupportedChainId } from 'constants/chains'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import useStakedCUSDBalance from 'hooks/useStakedChadBalance'
import { useUnstakeCallback } from 'hooks/useUnstakeCallback'
import JSBI from 'jsbi'
import { useCallback, useContext, useMemo, useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { Text } from 'rebass'
import { useDerivedUnstakeInfo, useUnstakeActionHandlers } from 'state/unstaking/hooks'
import styled, { ThemeContext } from 'styled-components/macro'
import { ThemedText } from 'theme'

import { BorrowCallbackError, Wrapper } from '../../components/Borrow/styleds'
import { ButtonError, ButtonLight } from '../../components/Button'
import { AutoColumn } from '../../components/Column'
import { SCUSD, UUSD } from '../../constants/tokens'
import { useWalletModalToggle } from '../../state/application/hooks'
import { useExpertModeManager } from '../../state/user/hooks'
import AppBody from '../AppBody'

const AlertWrapper = styled.div`
  max-width: 460px;
  width: 100%;
`
const HoverText = styled(ThemedText.Main)`
  text-decoration: none;
  color: ${({ theme }) => theme.text3};
  :hover {
    color: ${({ theme }) => theme.text1};
    text-decoration: none;
  }
`

export default function Unstaking({
  match: {
    params: { tokenId: tokenIdFromUrl },
  },
  history,
}: RouteComponentProps<{ tokenId?: string }>) {
  const { account, chainId } = useActiveWeb3React()

  const chadBalance = useStakedCUSDBalance(account ?? undefined)

  const maxStakeBalance = useMemo(() => {
    return chadBalance || CurrencyAmount.fromRawAmount(SCUSD[chainId || SupportedChainId.SONIC], '0')
  }, [chadBalance, chainId])

  const currency = SCUSD[chainId || SupportedChainId.SONIC]
  const { parsedAmount, inputError: borrowInputError } = useDerivedUnstakeInfo()

  const theme = useContext(ThemeContext)

  // toggle wallet when disconnected
  const toggleWalletModal = useWalletModalToggle()

  // for expert mode
  const [isExpertMode] = useExpertModeManager()

  const { onUserInput } = useUnstakeActionHandlers()
  const isValid =
    parsedAmount?.greaterThan(JSBI.BigInt(0)) &&
    (parsedAmount.lessThan(maxStakeBalance) || parsedAmount.equalTo(maxStakeBalance)) &&
    maxStakeBalance.greaterThan(JSBI.BigInt(0))

  const handleTypeInput = useCallback(
    (value: string) => {
      onUserInput(value)
    },
    [onUserInput]
  )

  const [{ showConfirm, borrowToConfirm, borrowErrorMessage, attemptingTxn, txHash }, setBorrowState] = useState<{
    showConfirm: boolean
    borrowToConfirm: CurrencyAmount<Currency> | undefined
    attemptingTxn: boolean
    borrowErrorMessage: string | undefined
    txHash: string | undefined
  }>({
    showConfirm: false,
    borrowToConfirm: CurrencyAmount.fromRawAmount(UUSD[chainId ? chainId : 1], JSBI.BigInt(0)),
    attemptingTxn: false,
    borrowErrorMessage: undefined,
    txHash: undefined,
  })

  // check whether the user has approved the router on the input token

  // check if user has gone through approval process, used to show two step buttons, reset on token change
  const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)

  const maxInputAmount: CurrencyAmount<Currency> | undefined = useMemo(() => maxStakeBalance, [maxStakeBalance])

  const showMaxButton = Boolean(maxStakeBalance?.greaterThan(0) && !parsedAmount?.equalTo(maxStakeBalance))

  const { state: borrowState, callback: borrowCallback } = useUnstakeCallback(parsedAmount)

  const handleBorrow = useCallback(async () => {
    if (!borrowCallback || !parsedAmount?.greaterThan(JSBI.BigInt(0))) {
      return
    }

    setBorrowState({
      attemptingTxn: true,
      borrowToConfirm: parsedAmount,
      showConfirm,
      borrowErrorMessage: undefined,
      txHash: undefined,
    })

    borrowCallback()
      .then((hash) => {
        setBorrowState({
          attemptingTxn: false,
          borrowToConfirm: parsedAmount,
          showConfirm,
          borrowErrorMessage: undefined,
          txHash: hash,
        })
      })
      .catch((error) => {
        setBorrowState({
          attemptingTxn: false,
          borrowToConfirm: parsedAmount,
          showConfirm,
          borrowErrorMessage: error.message,
          txHash: undefined,
        })
      })
  }, [borrowCallback, parsedAmount, showConfirm])
  const showApproveFlow = false

  const handleMaxInput = useCallback(() => {
    maxInputAmount && onUserInput(maxStakeBalance.toExact())
  }, [maxInputAmount, onUserInput])

  return (
    <>
      <AppBody>
        <UnstakeHeader />
        <Wrapper id="stake-page">
          {Boolean(!chadBalance) ? (
            <LoadingRows style={{ gap: '8px' }}>
              <div />
              <div />
              <div />
            </LoadingRows>
          ) : (
            <AutoColumn gap={'sm'}>
              <div style={{ display: 'relative' }}>
                <UnstakeInputPanel
                  label={<Trans>Unstake</Trans>}
                  value={parsedAmount?.toExact() || ''}
                  showMaxButton={showMaxButton}
                  currency={currency}
                  onUserInput={handleTypeInput}
                  onMax={handleMaxInput}
                  showCommonBases={true}
                  id="stake-input"
                  loading={!chadBalance}
                  maxStakeBalance={maxStakeBalance}
                />
              </div>

              <div>
                {!account ? (
                  <ButtonLight onClick={toggleWalletModal}>
                    <Trans>Connect Wallet</Trans>
                  </ButtonLight>
                ) : showApproveFlow ? (
                  <></>
                ) : (
                  <ButtonError
                    onClick={handleBorrow}
                    id="unstake-button"
                    disabled={!isValid || !chadBalance}
                    error={isValid}
                  >
                    <Text fontSize={20} fontWeight={500}>
                      {borrowInputError ? (
                        borrowInputError
                      ) : !chadBalance ? (
                        <Trans>Loading Balance</Trans>
                      ) : (
                        <Trans>Unstake</Trans>
                      )}
                    </Text>
                  </ButtonError>
                )}
                {isExpertMode && borrowErrorMessage ? <BorrowCallbackError error={borrowErrorMessage} /> : null}
              </div>
            </AutoColumn>
          )}
        </Wrapper>
      </AppBody>
      <AlertWrapper>
        <NetworkAlert />
      </AlertWrapper>
    </>
  )
}
